import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const { t, i18n } = useTranslation('common');
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleLanguage = () => {
    const newLang = currentLang === 'en' ? 'es' : 'en';
    i18n.changeLanguage(newLang);
    setCurrentLang(newLang);
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <div className="w-12 h-12 flex items-center justify-center">
              <img 
                src="https://static.readdy.ai/image/13ed06d0ea3cd7415a60a84f36bd9762/a73031cc809a317f27d0bbddc92453e1.png" 
                alt="BridgeOn Solutions Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <div className={`text-xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                BridgeOn Solutions
              </div>
              <div className={`text-xs ${isScrolled ? 'text-gray-600' : 'text-cyan-300'}`}>
                Digital Creative
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('services')}
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-cyan-600' : 'text-white hover:text-cyan-300'
              }`}
            >
              {t('nav.services')}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-cyan-600' : 'text-white hover:text-cyan-300'
              }`}
            >
              {t('nav.about')}
            </button>
            <button
              onClick={() => scrollToSection('why-us')}
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-cyan-600' : 'text-white hover:text-cyan-300'
              }`}
            >
              {t('nav.whyUs')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-gray-700 hover:text-cyan-600' : 'text-white hover:text-cyan-300'
              }`}
            >
              {t('nav.contact')}
            </button>

            {/* Language Switcher */}
            <button
              onClick={toggleLanguage}
              className={`flex items-center space-x-2 px-3 py-1.5 rounded-full border transition-all whitespace-nowrap ${
                isScrolled
                  ? 'border-gray-300 text-gray-700 hover:border-cyan-600 hover:text-cyan-600'
                  : 'border-white/30 text-white hover:border-cyan-300 hover:text-cyan-300'
              }`}
            >
              <i className="ri-global-line text-base"></i>
              <span className="text-sm font-medium">{currentLang.toUpperCase()}</span>
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <button className="md:hidden">
            <i className={`ri-menu-line text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
          </button>
        </div>
      </div>
    </header>
  );
}